/**
 * useLocationPolling - React hook for polling character location from ESI
 *
 * Polls the EVE ESI API to track:
 * - Current solar system
 * - Current ship type
 * - Online status
 *
 * Automatically detects system jumps for auto-mapping
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { apiUrl } from '../lib/config';

interface LocationData {
  systemId: number | null;
  systemName?: string;
  shipTypeId?: number;
  shipTypeName?: string;
  isOnline: boolean;
  lastUpdated: number;
}

interface UseLocationPollingOptions {
  /** Poll interval in milliseconds (default: 5000) */
  interval?: number;
  /** Enable polling (default: true when authenticated) */
  enabled?: boolean;
  /** Callback when system changes */
  onSystemChange?: (previousSystemId: number | null, newSystemId: number) => void;
  /** Callback when ship changes */
  onShipChange?: (previousShipTypeId: number | null, newShipTypeId: number) => void;
  /** Callback for location updates */
  onLocationUpdate?: (location: LocationData) => void;
}

interface UseLocationPollingReturn {
  location: LocationData | null;
  isPolling: boolean;
  error: string | null;
  startPolling: () => void;
  stopPolling: () => void;
  pollNow: () => Promise<void>;
}

const DEFAULT_INTERVAL = 5000; // 5 seconds

export function useLocationPolling(
  options: UseLocationPollingOptions = {}
): UseLocationPollingReturn {
  const {
    interval = DEFAULT_INTERVAL,
    enabled = true,
    onSystemChange,
    onShipChange,
    onLocationUpdate,
  } = options;

  const { character } = useAuthStore();
  const [location, setLocation] = useState<LocationData | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousSystemIdRef = useRef<number | null>(null);
  const previousShipTypeIdRef = useRef<number | null>(null);

  // Fetch location data from ESI proxy
  const fetchLocation = useCallback(async (): Promise<LocationData | null> => {
    if (!character) {
      return null;
    }

    try {
      // Fetch location, ship, and online status in parallel
      const [locationRes, shipRes, onlineRes] = await Promise.all([
        fetch(apiUrl(`/api/esi/location/${character.id}`), { credentials: 'include' }),
        fetch(apiUrl(`/api/esi/ship/${character.id}`), { credentials: 'include' }),
        fetch(apiUrl(`/api/esi/online/${character.id}`), { credentials: 'include' }),
      ]);

      let systemId: number | null = null;
      let shipTypeId: number | undefined;
      let isOnline = false;

      if (locationRes.ok) {
        const locationData = await locationRes.json();
        systemId = locationData.solar_system_id;
      }

      if (shipRes.ok) {
        const shipData = await shipRes.json();
        shipTypeId = shipData.ship_type_id;
      }

      if (onlineRes.ok) {
        const onlineData = await onlineRes.json();
        isOnline = onlineData.online || false;
      }

      return {
        systemId,
        shipTypeId,
        isOnline,
        lastUpdated: Date.now(),
      };
    } catch (err) {
      console.error('Failed to fetch location:', err);
      throw err;
    }
  }, [character]);

  // Poll for location updates
  const pollNow = useCallback(async () => {
    try {
      setError(null);
      const newLocation = await fetchLocation();

      if (newLocation) {
        setLocation(newLocation);
        onLocationUpdate?.(newLocation);

        // Check for system change
        if (
          newLocation.systemId &&
          newLocation.systemId !== previousSystemIdRef.current
        ) {
          onSystemChange?.(previousSystemIdRef.current, newLocation.systemId);
          previousSystemIdRef.current = newLocation.systemId;
        }

        // Check for ship change
        if (
          newLocation.shipTypeId &&
          newLocation.shipTypeId !== previousShipTypeIdRef.current
        ) {
          onShipChange?.(previousShipTypeIdRef.current, newLocation.shipTypeId);
          previousShipTypeIdRef.current = newLocation.shipTypeId;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [fetchLocation, onLocationUpdate, onSystemChange, onShipChange]);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      return; // Already polling
    }

    setIsPolling(true);

    // Poll immediately
    pollNow();

    // Set up interval
    intervalRef.current = setInterval(pollNow, interval);
  }, [pollNow, interval]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Auto-start/stop polling based on enabled prop and auth state
  useEffect(() => {
    if (enabled && character) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enabled, character, startPolling, stopPolling]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    location,
    isPolling,
    error,
    startPolling,
    stopPolling,
    pollNow,
  };
}

export default useLocationPolling;
